import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { connect } from 'react-redux';

import { DateStringMMDDYYYY, } from '../actions/types';
import { format } from 'date-fns';

import PushButton from './PushButton';

import BackIcon from '../icons/backIcon';

const CalendarPicker = ({ selectedDate, onDateChange, onSubmit }) => {

  const [cursorDate, setCursorDate] = useState(format(DateStringMMDDYYYY.toDateObject(selectedDate).getTime(), 'MM/dd/yyyy'));
  const [currentMonthDate, setCurrentMonthDate] = useState(DateStringMMDDYYYY.toDateObject(selectedDate));

  const inputDateRef = useRef(null);
  const calendarRef = useRef(null);
  const dragX = useRef(null); // drag stuff is used to detect swipe left/right
  const dragStartX = useRef(null);
  const dragStartTime = useRef(null);

  function getMouseCoords(event) {
    var coords = [];
    if (event.type.indexOf('touch') >= 0) {
      if (event.touches && event.touches.length) { 	// iPhone
        coords[0] = event.touches[0].clientX;
        coords[1] = event.touches[0].clientY;
      } else { 								// all others
        coords[0] = event.clientX;
        coords[1] = event.clientY;
      }
    } else {
      if (event.pageX == null) { // IE case
        var doc = (document.documentElement && document.documentElement.scrollLeft != null) ? document.documentElement : document.body;
        coords[0] = event.clientX + doc.scrollLeft;
        coords[1] = event.clientY + doc.scrollTop;
      } else { // all other browsers
        coords[0] = event.pageX;
        coords[1] = event.pageY;
      }
    }
    return coords;
  };

  const goToPreviousMonth = useCallback(() => {
    const newCurrentCalendarDate = new Date(currentMonthDate.getTime());
    newCurrentCalendarDate.setDate(1); // set to first day of month before incrementing month. if we're on day 31 and increment to a month with 30 days it will skip a month
    newCurrentCalendarDate.setMonth(newCurrentCalendarDate.getMonth() - 1);
    setCurrentMonthDate(newCurrentCalendarDate);
  }, [currentMonthDate]);

  const goToNextMonth = useCallback(() => {
    const newCurrentCalendarDate = new Date(currentMonthDate.getTime());
    newCurrentCalendarDate.setDate(1); // set to first day of month before incrementing month. if we're on day 31 and increment to a month with 30 days it will skip a month
    newCurrentCalendarDate.setMonth(newCurrentCalendarDate.getMonth() + 1);
    setCurrentMonthDate(newCurrentCalendarDate);
  }, [currentMonthDate]);

  useEffect(() => {
    // setup mouse/touch listeners to detect swipe right/left gestures
    const startFling = (e) => {
      dragX.current = dragStartX.current = getMouseCoords(e)[0];
      dragStartTime.current = new Date().getTime();
    };

    const moveFling = (e) => {
      dragX.current = getMouseCoords(e)[0];
    };

    const endFling = (e) => {
      if(Math.abs(dragStartX.current - dragX.current) > 30 && (new Date().getTime() - dragStartTime.current) < 500) {
        if(dragX.current > dragStartX.current) {
          const newCurrentCalendarDate = new Date(currentMonthDate.getTime());
          newCurrentCalendarDate.setDate(1); // set to first day of month before incrementing month. if we're on day 31 and increment to a month with 30 days it will skip a month
          newCurrentCalendarDate.setMonth(newCurrentCalendarDate.getMonth() - 1);
          setCurrentMonthDate(newCurrentCalendarDate);
        } else {
          const newCurrentCalendarDate = new Date(currentMonthDate.getTime());
          newCurrentCalendarDate.setDate(1); // set to first day of month before incrementing month. if we're on day 31 and increment to a month with 30 days it will skip a month
          newCurrentCalendarDate.setMonth(newCurrentCalendarDate.getMonth() + 1);
          setCurrentMonthDate(newCurrentCalendarDate);
          //if(calendarRef.current !== null) {
          //  calendarRef.current.focus();
          //}
        }
      }
    };

    const onKeyDown = (event) => {
      if(event.key === 'ArrowLeft') { 
        const newCursorDate = new Date(DateStringMMDDYYYY.toDateObject(cursorDate));
        newCursorDate.setDate(newCursorDate.getDate()-1);
        setCursorDate(format(newCursorDate, 'MM/dd/yyyy'));
        setCurrentMonthDate(newCursorDate);
      } else if(event.key === 'ArrowRight') { 
        const newCursorDate = new Date(DateStringMMDDYYYY.toDateObject(cursorDate));
        newCursorDate.setDate(newCursorDate.getDate()+1);
        setCursorDate(format(newCursorDate, 'MM/dd/yyyy'));
        setCurrentMonthDate(newCursorDate);
      } else if(event.key === 'ArrowDown') { 
        const newCursorDate = new Date(DateStringMMDDYYYY.toDateObject(cursorDate));
        newCursorDate.setDate(newCursorDate.getDate()+7);
        setCursorDate(format(newCursorDate, 'MM/dd/yyyy'));
        setCurrentMonthDate(newCursorDate);
      } else if(event.key === 'ArrowUp') { 
        const newCursorDate = new Date(DateStringMMDDYYYY.toDateObject(cursorDate));
        newCursorDate.setDate(newCursorDate.getDate()-7);
        setCursorDate(format(newCursorDate, 'MM/dd/yyyy'));
        setCurrentMonthDate(newCursorDate);
      } else if(event.key === 'Enter') { 
        try {
          onSubmit(DateStringMMDDYYYY.validate(cursorDate));
        } catch(error) {}
      } 
    };

    const savedCalendarRef = calendarRef.current;

    if(calendarRef.current !== null) {
      window.addEventListener('keydown', onKeyDown);
      calendarRef.current.addEventListener('mousedown', startFling);
      calendarRef.current.addEventListener('mousemove', moveFling);
      calendarRef.current.addEventListener('mouseup', endFling);
      
      calendarRef.current.addEventListener('touchstart', startFling);
      calendarRef.current.addEventListener('touchmove', moveFling);
      calendarRef.current.addEventListener('touchend', endFling);
    }
    return () => {
      if(savedCalendarRef !== null) {
        window.removeEventListener('keydown', onKeyDown);
        savedCalendarRef.removeEventListener('mousedown', startFling);
        savedCalendarRef.removeEventListener('mousemove', moveFling);
        savedCalendarRef.removeEventListener('mouseup', endFling);

        savedCalendarRef.removeEventListener('touchstart', startFling);
        savedCalendarRef.removeEventListener('touchmove', moveFling);
        savedCalendarRef.removeEventListener('touchend', endFling);
      }
    };
  }, [currentMonthDate, goToNextMonth, goToPreviousMonth, cursorDate, onSubmit]);

  useEffect(() => {
    try {
      onDateChange(cursorDate);
    } catch(error) {}
  }, [cursorDate, onDateChange]);

  const getCalendarElement = useCallback(() => {
    const startMonthDate = new Date(currentMonthDate.getTime());
    startMonthDate.setDate(1); //set to first day of month
    startMonthDate.setDate(startMonthDate.getDate() - startMonthDate.getDay()); //set to first day of week

    const endMonthDate = new Date(currentMonthDate.getTime());
    endMonthDate.setDate(1); // set to first day of month before incrementing month. if we're on day 31 and increment to a month with 30 days it will skip a month
    endMonthDate.setMonth(endMonthDate.getMonth() + 1); //set to next month
    endMonthDate.setDate(0); //set to last day of month previous month (last day of this month)
    endMonthDate.setDate(endMonthDate.getDate() + (6 - endMonthDate.getDay())); //set to last day of week

    const daysInMonth = [];
    const itDay = new Date(startMonthDate.getTime());
    const currentDatestring = format(new Date(), 'MMM d, yyyy');
    while(itDay.getTime() <= endMonthDate.getTime()) {
      const datestring = format(itDay, 'MMM d, yyyy');
      daysInMonth.push({ 
        dayOfMonth: itDay.getDate(),
        inMonth: itDay.getMonth() === currentMonthDate.getMonth(),
        isToday: datestring === currentDatestring,
        timestamp: itDay.getTime(),
        dateString: format(itDay.getTime(), 'MM/dd/yyyy'),
      });
      itDay.setDate(itDay.getDate() + 1);
    }

    const cursorDateObject = DateStringMMDDYYYY.toDateObject(cursorDate);
    const dayOfWeekHeaders = [];
    const selectedDayOfWeek = format(cursorDateObject, 'iii');
    const cursorShowing = cursorDateObject.getTime() >= startMonthDate.getTime() && cursorDateObject.getTime() <= endMonthDate.getTime();
    for(const dayOfWeek of ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']) {
      dayOfWeekHeaders.push({
        dayOfWeek: dayOfWeek,
        isSelected: dayOfWeek === selectedDayOfWeek && cursorShowing,
      });
    }

    return (
      <div 
        className='h-full flex flex-col relative text-white select-none'
        onWheel={(event) => {
          // deltaY is positive if scrolling down, negative if scrolling up
          const { deltaY, deltaX } = event;
          if(deltaY > 0) {
            goToPreviousMonth();
          } else {
            goToNextMonth();
          }
        }}
      >
        <div className='w-full flex justify-around px-4'>
          <div>
            <PushButton 
              inline={true}
              textColor='white'
              bgColor='bg-stone-600'
              hoverBgColor='bg-stone-500'
              onClick={() => { 
                goToPreviousMonth();
              }}
            >
              <BackIcon color='#fff' />
            </PushButton>
          </div>
          <div className='font-bold my-2 text-lg grow-[1] whitespace-nowrap text-center'>
            {format(currentMonthDate, 'MMMM yyyy')}
          </div>
          <div>
            <PushButton 
              inline={true}
              textColor='white'
              bgColor='bg-stone-600'
              hoverBgColor='bg-stone-500'
              onClick={() => { 
                goToNextMonth();
              }}
            >
              <div className='rotate-180'>
                <BackIcon color='#fff' />
              </div>
            </PushButton>
          </div>
        </div>
        <div 
          ref={calendarRef}
          className={'grid grid-cols-7 auto-rows-fr justify-items-center mx-auto w-full h-full grid-template-rows: 0.5em repeat(auto-fill, minmax(100px, 1fr));'}
        >
          {dayOfWeekHeaders.map((dayOfWeek, index) => (
            <div key={index} className={`text-sm font-medium flex flex-col justify-center items-center ${dayOfWeek.isSelected ? 'text-[#f79800]' : ''}`}>{dayOfWeek.dayOfWeek}</div>
          ))}
          {daysInMonth.map((day, index) => (
            <button 
              key={index} 
              className={`w-full h-full flex flex-col justify-center items-center rounded-md hover:bg-[#f7980011] outline-[#f79800] active:outline-yellow-500 active:outline active:outline-2 active:outline-offset-[-1px] ${day.dateString === cursorDate ? 'outline' : ''}`} 
               onClick={() => { 
                 setCursorDate(format(new Date(day.timestamp), 'MM/dd/yyyy'));
               }}
              >
              {day.isToday &&
                <div className='w-full text-center font-bold text-xs pl-0.5 text-[#f79800]'>Today</div>
              }
              <span className={`text-white ${day.inMonth ? '' : 'opacity-40'} ${day.dateString === cursorDate ? 'text-xl' : ''}`}>{day.dayOfMonth}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }, [currentMonthDate, cursorDate, goToPreviousMonth, goToNextMonth]);

  return getCalendarElement();
};

// define redux state that will be passed in as props
const mapStateToProps = state => { return { }};
export default connect(mapStateToProps, { })(CalendarPicker);
